/**
 * BookTimeline.js — compact horizontal status history for a book.
 *
 * Renders the events returned by GET /api/admin/books/{id}/timeline as a
 * left-to-right chain: "Ingedien 5 dae gelede → Teruggestuur deur Marthie 3
 * dae gelede → Herlaai gister → Wag op proeflees".
 *
 *   • Each event is a pill (icon + label + relative-time + actor).
 *   • Latest event is highlighted amber.
 *   • Lazy-loaded — fetches only when the card is expanded.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Upload, Eye, Send, Bell, RefreshCw, Check, Sparkles, ChevronRight, Loader2, History as HistoryIcon } from 'lucide-react';

const ICON_MAP = {
  upload: Upload,
  eye: Eye,
  send: Send,
  bell: Bell,
  refresh: RefreshCw,
  check: Check,
  sparkles: Sparkles,
};

const relTime = (days) => {
  if (days === null || days === undefined) return '';
  if (days === 0) return 'vandag';
  if (days === 1) return 'gister';
  return `${days} dae gelede`;
};

export default function BookTimeline({ bookId, apiBase }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${apiBase}/admin/books/${bookId}/timeline`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bookId, apiBase]);

  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
      <Loader2 className="h-3 w-3 animate-spin" /> Laai tydlyn…
    </div>;
  }
  const events = (data && data.events) || [];
  if (!events.length) {
    return <p className="text-xs text-gray-400 italic">Geen tydlyn beskikbaar nie.</p>;
  }

  return (
    <div className="flex items-center gap-1 flex-wrap text-[11px]" data-testid={`book-timeline-${bookId}`}>
      <HistoryIcon className="h-3 w-3 text-gray-400 flex-shrink-0 mr-1" />
      {events.map((ev, i) => {
        const Icon = ICON_MAP[ev.icon] || Upload;
        const isLast = i === events.length - 1;
        return (
          <React.Fragment key={`${ev.at}-${i}`}>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${
                isLast ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-gray-100 text-gray-700'
              }`}
              title={`${ev.label}${ev.actor ? ` deur ${ev.actor}` : ''}${ev.detail ? ` — ${ev.detail}` : ''} (${ev.at})`}
              data-testid={`timeline-event-${bookId}-${i}`}
            >
              <Icon className="h-3 w-3 flex-shrink-0" />
              <span className="font-medium">{ev.label}</span>
              {ev.actor && <span className="text-gray-500">· {ev.actor}</span>}
              <span className="text-gray-400">· {relTime(ev.days_since)}</span>
            </span>
            {!isLast && <ChevronRight className="h-3 w-3 text-gray-300 flex-shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
